import React, { useState, useContext } from 'react';
import { Box, Container } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import projects from '../data/project-data';
import AuthContext from '../store/AuthContext';

const allTags = ['All', ...new Set(projects.flatMap(p => p.tags).filter(t => t !== 'Spotlight'))];

const ProjectsSection = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [activeFilter, setActiveFilter] = useState('All');

  const filtered = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.tags.includes(activeFilter));

  return (
    <Box id="projects" component="section" sx={{ borderBottom: '1px solid #1E3040', background: '#0D1821' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: '-80px' }}
        >
          <Box sx={{
            fontSize: '10px', letterSpacing: '4px', textTransform: 'uppercase',
            color: '#FF914D', fontFamily: "'Barlow', sans-serif", mb: 2,
            display: 'flex', alignItems: 'center', gap: 1.5,
            '&::before': { content: '""', display: 'block', width: '24px', height: '1px', background: '#FF914D' },
          }}>
            Selected Work
          </Box>
          <Box component="h2" sx={{
            fontFamily: "'Anton', sans-serif",
            fontSize: { xs: '48px', md: '60px' },
            textTransform: 'uppercase', lineHeight: 0.92,
            color: '#F0EEE8', m: 0, mb: 2.5,
          }}>
            Recent<br />Projects
          </Box>
          <Box component="p" sx={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontStyle: 'italic', fontWeight: 300,
            fontSize: '16px', color: '#7A9AB0', lineHeight: 1.7,
            maxWidth: '520px', m: 0,
          }}>
            A selection of work built for real clients and personal ventures.
          </Box>
        </motion.div>

        {/* Filter pills */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 6, mb: 4 }}>
          {allTags.map(tag => (
            <Box
              key={tag}
              component="button"
              onClick={() => setActiveFilter(tag)}
              sx={{
                px: 2.5, py: 1,
                fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase',
                fontFamily: "'Barlow', sans-serif", fontWeight: 400,
                border: '1px solid',
                borderColor: activeFilter === tag ? '#FF914D' : '#1E3040',
                color: activeFilter === tag ? '#FF914D' : '#7A9AB0',
                background: activeFilter === tag ? 'rgba(255,145,77,0.08)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#FF914D', color: '#FF914D' },
              }}
            >
              {tag}
            </Box>
          ))}
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25 }}
          >
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: '1px',
              background: '#1E3040',
            }}>
              {filtered.length === 0 ? (
                <Box sx={{ background: '#162333', p: 8, gridColumn: '1/-1', textAlign: 'center' }}>
                  <Box sx={{ fontFamily: "'Source Serif 4', serif", fontStyle: 'italic', color: '#7A9AB0' }}>
                    No projects found.
                  </Box>
                </Box>
              ) : filtered.map((project, i) => (
                <motion.div
                  key={project.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  viewport={{ once: true, margin: '-40px' }}
                >
                  <Box sx={{
                    background: '#162333', p: { xs: 4, md: 5 },
                    display: 'flex', flexDirection: 'column', gap: 2,
                    height: '100%',
                    position: 'relative', overflow: 'hidden',
                    transition: 'background 0.2s',
                    '&:hover': { background: '#1a2d3f' },
                    '&:hover .proj-arrow': { opacity: 1, transform: 'translateX(0)' },
                    '&:hover .proj-img': { opacity: 1 },
                  }}>
                    <Box className="proj-arrow" sx={{
                      position: 'absolute', top: 32, right: 32,
                      fontSize: '20px', color: '#FF914D',
                      opacity: 0, transform: 'translateX(-8px)',
                      transition: 'opacity 0.2s, transform 0.2s',
                    }}>
                      →
                    </Box>

                    <Box sx={{
                      fontSize: '9px', letterSpacing: '3px', textTransform: 'uppercase',
                      color: '#FF914D', fontFamily: "'Barlow', sans-serif",
                    }}>
                      {project.tags.filter(t => t !== 'Spotlight')[0] || project.tags[0]}
                    </Box>

                    <Box component="h3" sx={{
                      fontFamily: "'Anton', sans-serif", fontSize: '26px',
                      textTransform: 'uppercase', color: '#F0EEE8',
                      lineHeight: 1, m: 0,
                    }}>
                      {project.name}
                    </Box>

                    <Box component="p" sx={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontStyle: 'italic', fontWeight: 300,
                      fontSize: '14px', color: '#7A9AB0', lineHeight: 1.7, m: 0,
                    }}>
                      {project.description}
                    </Box>

                    {project.screenshot && (
                      <Box
                        className="proj-img"
                        component="img"
                        src={project.screenshot}
                        alt={project.name}
                        sx={{
                          width: '100%', height: '160px',
                          objectFit: 'cover',
                          border: '1px solid #1E3040',
                          opacity: 0.7,
                          transition: 'opacity 0.2s',
                          mt: 'auto',
                        }}
                      />
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {project.liveUrl && (
                        <Box
                          component="a"
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener"
                          sx={{
                            fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase',
                            color: '#FF914D', fontFamily: "'Barlow', sans-serif",
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          Live Demo →
                        </Box>
                      )}
                      {project.repoLink && (
                        <Box
                          component="a"
                          href={project.repoLink}
                          target="_blank"
                          rel="noopener"
                          sx={{
                            fontSize: '9px', letterSpacing: '2px', textTransform: 'uppercase',
                            color: '#7A9AB0', fontFamily: "'Barlow', sans-serif",
                            textDecoration: 'none',
                            '&:hover': { color: '#F0EEE8', textDecoration: 'underline' },
                          }}
                        >
                          Code →
                        </Box>
                      )}
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default ProjectsSection;
